import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { getRegistrationSessionByToken } from '../services/registrationSession';

/**
 * Context referral disponibile in tutti gli step di registrazione.
 *
 * Strategia:
 * 1. Se la chiave sessionStorage `flusia-registration-context` esiste, usa quella
 *    (fast path, zero network)
 * 2. Altrimenti: SELECT registration_sessions (anon ha gia accesso), legge
 *    partner_data + referral_data, chiama RPC `lookup_referral_code` per il nome
 *    visualizzato, deriva trial_days. Salva in sessionStorage e ritorna.
 *
 * Trial days breakdown (deve restare allineato a register-complete edge fn):
 *   - Regular signup: 30 giorni
 *   - Partner referral: 30 (approssimazione; il backend usa il valore configurato
 *     per partner, qui mostriamo 30 come default visibile)
 *   - Org-to-org referral: 30 giorni
 */
export interface RegistrationContext {
  loading: boolean;
  trialDays: number;
  referrerName: string | null;
  referrerType: 'partner' | 'organization' | null;
}

const STORAGE_KEY = 'flusia-registration-context';

interface CachedContext {
  trialDays: number;
  referrerName: string | null;
  referrerType: 'partner' | 'organization' | null;
}

function readCache(): CachedContext | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedContext;
    if (typeof parsed.trialDays !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(ctx: CachedContext): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ctx));
  } catch {
    // ignore (quota / private mode)
  }
}

/**
 * Usabile sia con sessionToken (DB lookup) sia con un codice referral grezzo
 * (lookup diretto pre-session, usato da RegisterPage che non ha ancora un token).
 */
export function useRegistrationContext(
  source: { sessionToken?: string | null; rawCode?: string | null },
): RegistrationContext {
  const [state, setState] = useState<RegistrationContext>(() => {
    const cached = readCache();
    return cached
      ? { loading: false, ...cached }
      : { loading: true, trialDays: 14, referrerName: null, referrerType: null };
  });

  const sessionToken = source.sessionToken ?? null;
  const rawCode = source.rawCode ?? null;

  useEffect(() => {
    let cancelled = false;

    const finalize = (ctx: CachedContext) => {
      if (cancelled) return;
      writeCache(ctx);
      setState({ loading: false, ...ctx });
    };

    const defaultCtx: CachedContext = {
      trialDays: 14,
      referrerName: null,
      referrerType: null,
    };

    (async () => {
      // Path A: codice ref grezzo (RegisterPage step 1, no token ancora)
      if (rawCode && rawCode.trim()) {
        try {
          const { data } = await supabase.rpc('lookup_referral_code', { p_code: rawCode });
          const row = (Array.isArray(data) ? data[0] : null) as
            | { valid?: boolean; ref_type?: string; ref_name?: string }
            | null;
          if (row?.valid && row.ref_name) {
            const type = row.ref_type === 'partner' ? 'partner' : 'organization';
            finalize({
              trialDays: 30,
              referrerName: row.ref_name,
              referrerType: type,
            });
            return;
          }
        } catch {
          // continua, finalizza come default
        }
        finalize(defaultCtx);
        return;
      }

      // Path B: sessionToken (step 2+, leggi la session)
      if (!sessionToken) {
        finalize(defaultCtx);
        return;
      }

      // Se gia in cache evitiamo la query
      const cached = readCache();
      if (cached) {
        if (!cancelled) setState({ loading: false, ...cached });
        return;
      }

      try {
        const { data: session } = await getRegistrationSessionByToken(sessionToken);

        if (!session) {
          finalize(defaultCtx);
          return;
        }

        const partnerCode = (session.partner_data as { referralCode?: string } | null)?.referralCode;
        const orgRefCode = (session.referral_data as { referral_code?: string } | null)?.referral_code;
        const lookupCode = partnerCode ?? orgRefCode ?? null;

        if (!lookupCode) {
          finalize(defaultCtx);
          return;
        }

        const { data } = await supabase.rpc('lookup_referral_code', { p_code: lookupCode });
        const row = (Array.isArray(data) ? data[0] : null) as
          | { valid?: boolean; ref_type?: string; ref_name?: string }
          | null;

        if (row?.valid && row.ref_name) {
          const type = row.ref_type === 'partner' ? 'partner' : 'organization';
          finalize({
            trialDays: 30,
            referrerName: row.ref_name,
            referrerType: type,
          });
          return;
        }

        finalize(defaultCtx);
      } catch {
        finalize(defaultCtx);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionToken, rawCode]);

  return state;
}

/**
 * Helper: pulisci il cache di registrazione (fine flusso, abbandono).
 * Usato da CompletePage o da logout post-registration per evitare leak in tab
 * successivi.
 */
export function clearRegistrationContext(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
